// Shared component (TS-2.5.03). Wires one form control to its label, an optional description and
// an optional error, generating the ids ARIA needs (aria-describedby / aria-invalid) rather than
// leaving every form in the app to reinvent that wiring - and the reinventing is exactly what
// batch 3's project/milestone/note forms would otherwise have done four times.
//
// Render order is label, control, description, error (D-295: the description used to sit between
// label and control, which pushed the control itself out of row alignment with its neighbours in
// grid-based forms - e.g. ProjectForm's "% complete" field sat one line lower than the fields
// beside it). DateField's own `interpretation` note already renders below its control, so this
// also brings FormField's description in line with that existing convention. aria-describedby
// wiring is unaffected: only DOM order moves, the ids are unchanged.
import type { ReactNode } from "react";

export interface FormFieldProps {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly error?: string | null;
  readonly required?: boolean;
  readonly children: ReactNode;
}

export function FormField({ id, label, description, error, required, children }: FormFieldProps) {
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="eg-form-field">
      <label htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {/* Cloning is deliberately avoided: the control passed as children owns its own id/
         aria-describedby/aria-invalid wiring at the call site, this component only computes the
         values so every call site derives them the same way. */}
      <div className="eg-form-field__control" data-describedby={describedBy} data-invalid={Boolean(error)}>
        {children}
      </div>
      {description && (
        <p id={descriptionId} className="eg-form-field__description">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="eg-form-field__error">
          {error}
        </p>
      )}
    </div>
  );
}
